import React from 'react';
import { Badge, Box, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { TeamChatConversation } from '../../../services/teamChatService';
import TeamChatAvatar from '../components/TeamChatAvatar';
import { DOCK_CSS } from '../../../components/layout/floatingDockLayout';
import {
  getTeamChatDockZ,
  TEAM_CHAT_THEME,
} from '../teamChatTheme';

interface TeamChatMinimizedBarProps {
  conversations: TeamChatConversation[];
  expandedIds: string[];
  focusedId: string | null;
  bottomOffset?: number;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
}

const TeamChatMinimizedBar: React.FC<TeamChatMinimizedBarProps> = ({
  conversations,
  expandedIds,
  focusedId,
  bottomOffset = 16,
  onSelect,
  onClose,
}) => {
  const theme = useTheme();
  const dockZ = getTeamChatDockZ(theme);

  if (!conversations.length) return null;

  return (
    <Box
      data-team-chat-surface="true"
      sx={{
        position: 'fixed',
        right: `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.teamChatCardsRight})`,
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)`,
        zIndex: dockZ.dockBar,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 0.75,
        py: 0.25,
        width: 'max-content',
        maxWidth: `calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - ${DOCK_CSS.teamChatCardsRight} - 24px)`,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {conversations.map((conversation) => {
        const isExpanded = expandedIds.includes(conversation.id);
        const isFocused = focusedId === conversation.id;
        const counterpart = conversation.counterpart;

        return (
          <Paper
            key={conversation.id}
            elevation={0}
            onClick={() => onSelect(conversation.id)}
            sx={{
              height: 44,
              minWidth: 176,
              maxWidth: 230,
              flexShrink: 0,
              px: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
              borderRadius: 999,
              bgcolor: isExpanded && isFocused ? TEAM_CHAT_THEME.primarySoft : '#fff',
              border: `1px solid ${
                isExpanded && isFocused ? TEAM_CHAT_THEME.primary : TEAM_CHAT_THEME.border
              }`,
              boxShadow: isExpanded
                ? '0 10px 28px rgba(37,99,235,.18)'
                : '0 8px 24px rgba(15,23,42,.16)',
              transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 30px rgba(15,23,42,.2)',
              },
            }}
          >
            <Badge
              color="error"
              badgeContent={conversation.unread_count}
              max={99}
              overlap="circular"
            >
              <TeamChatAvatar
                name={conversation.title}
                avatarUrl={conversation.avatar_url}
                gender={counterpart?.gender}
                isGroup={conversation.type === 'group'}
                size={30}
              />
            </Badge>
            <Typography
              noWrap
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: '0.76rem',
                fontWeight: conversation.unread_count || (isExpanded && isFocused) ? 700 : 600,
                color: TEAM_CHAT_THEME.navy,
              }}
            >
              {conversation.title}
            </Typography>
            <Tooltip title="Fechar conversa">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose(conversation.id);
                }}
                sx={{ width: 26, height: 26 }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        );
      })}
    </Box>
  );
};

export default TeamChatMinimizedBar;
